import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { supabase } from '../../lib/supabase';
import { completeProfile } from '../../shared/auth/account-api';
import { applyAccountStatus } from '../../shared/hooks/useAccountStatus';
import { Button } from '../../shared/ui/Button';
import { Dialog, DialogBody, DialogFooter, DialogHeader } from '../../shared/ui/Dialog';
import { FormError } from '../../shared/ui/FormError';
import { Input } from '../../shared/ui/Input';
import { Label } from '../../shared/ui/Label';
import { PhoneField } from '../../shared/ui/PhoneField';
import { StatusBanner } from '../../shared/ui/StatusBanner';

import s from './CompleteProfileDialog.module.css';
import { completeProfileSchema, type CompleteProfileInput } from './schemas';
import { signOutAndReset } from './sign-out';

interface CompleteProfileDialogProps {
  /** Whose status to publish once the server accepts the details. */
  userId: string;
  initialName: string;
  initialPhone: string;
  /** Shown so the user can tell which account they are completing. */
  accountLabel: string;
}

/** A name we can show back instead of asking for — Google's, or one already stored. */
function isUsableName(name: string): boolean {
  return completeProfileSchema.shape.display_name.safeParse(name).success;
}

/**
 * The blocking onboarding step.
 *
 * Email/password signup collects neither a name nor a phone, and every account
 * provisioned by an institute was stored with phone = ''. Rather than exempt
 * those users, we ask once, here, and refuse to render the app until it is
 * answered — an institute that cannot phone its students has a half-useful
 * roster.
 *
 * Google sign-in is the exception worth handling: it already gives us a name, so
 * re-typing it is busywork. When we arrive with a usable name the field
 * collapses to a "Name — Sita Sharma [Change]" line and the user is asked for
 * one thing, the phone. The value still travels through the same validated
 * field, so a name we somehow got wrong is always correctable.
 *
 * Non-dismissible by design, so it MUST offer a way out: "Sign out" is always
 * present. A modal with no escape is a trap, not a gate.
 */
export function CompleteProfileDialog({
  userId,
  initialName,
  initialPhone,
  accountLabel,
}: CompleteProfileDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);
  // The gate unmounts this dialog on the render after the status is published.
  // Without this the button would snap back to idle for that beat, which reads
  // as "nothing happened".
  const [saved, setSaved] = useState(false);
  const [editingName, setEditingName] = useState(!isUsableName(initialName));

  const {
    register,
    control,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CompleteProfileInput>({
    resolver: zodResolver(completeProfileSchema),
    defaultValues: { display_name: initialName, phone: initialPhone },
  });

  // A hidden field whose value is rejected would otherwise block submit with an
  // error nobody can see. Any complaint about the name forces it back on screen.
  const showNameInput = editingName || errors.display_name !== undefined;
  const knownName = watch('display_name');

  async function onSubmit(values: CompleteProfileInput): Promise<void> {
    setSubmitError(null);
    try {
      // The RPC returns the FULL account status, so the gate can be updated from
      // this answer alone — no invalidate-then-refetch, which is the step that
      // used to be able to fail silently and leave this button spinning.
      const status = await completeProfile(values.display_name, values.phone);
      // The RPC writes profiles.phone; user_metadata.phone is what otp-send
      // reads (_shared/auth.ts). Writing both is what keeps the OTP gate
      // working for email accounts with no Edge Function redeploy.
      const { error } = await supabase.auth.updateUser({
        data: { name: values.display_name, phone: values.phone },
      });
      if (error !== null) throw new Error(error.message);

      if (!status.profileComplete) {
        // The server accepted the write but still reports the profile as
        // incomplete — the only state in which publishing would leave this
        // dialog mounted forever. Say so instead of spinning.
        setSubmitError(
          'Your details were accepted but your profile still reads as incomplete. Try again, and if it keeps happening ask your institute to check your account.',
        );
        return;
      }

      setSaved(true);
      applyAccountStatus(queryClient, userId, status);
    } catch (err) {
      setSaved(false);
      setSubmitError(
        err instanceof Error ? err.message : 'Could not save your details. Please try again.',
      );
    }
  }

  // No navigate() needed: signing out flips the session to null, and the gate
  // that renders this dialog redirects to /login on the next render.
  async function handleSignOut(): Promise<void> {
    await signOutAndReset();
  }

  return (
    <div className={s.backdrop}>
      <Dialog
        open
        dismissible={false}
        onClose={() => {
          /* blocking gate — dismissal is handled by Dialog's dismissible=false */
        }}
        labelledById="complete-profile-title"
        describedById="complete-profile-desc"
        size="sm"
      >
        <DialogHeader
          titleId="complete-profile-title"
          title="One more thing"
          subtitle={`Finish setting up ${accountLabel} so your institute can reach you.`}
        />

        <DialogBody>
          <form
            id="complete-profile-form"
            noValidate
            onSubmit={(event) => {
              void handleSubmit(onSubmit)(event);
            }}
            className={s.form}
          >
            <p id="complete-profile-desc" className={s.lede}>
              {showNameInput
                ? 'We ask once. Your tutors use these details to identify you and share results.'
                : 'We ask once. Your tutors use your number to reach you about sessions and results.'}
            </p>

            {showNameInput ? (
              <div>
                <Label htmlFor="cp-name">Full name</Label>
                <Input
                  id="cp-name"
                  autoComplete="name"
                  placeholder="Sita Sharma"
                  invalid={errors.display_name !== undefined}
                  aria-describedby={errors.display_name ? 'cp-name-error' : undefined}
                  {...register('display_name')}
                />
                <FormError id="cp-name-error" message={errors.display_name?.message} />
              </div>
            ) : (
              <div className={s.identity}>
                <span className={s.identityLabel}>Name</span>
                <span className={s.identityValue}>{knownName}</span>
                <button
                  type="button"
                  className={s.change}
                  aria-label="Change your name"
                  onClick={() => setEditingName(true)}
                >
                  Change
                </button>
                <input type="hidden" {...register('display_name')} />
              </div>
            )}

            <div>
              <Label htmlFor="cp-phone">Mobile number</Label>
              <Controller
                control={control}
                name="phone"
                render={({ field }) => (
                  <PhoneField
                    id="cp-phone"
                    value={field.value}
                    onChange={field.onChange}
                    invalid={errors.phone !== undefined}
                    describedBy={errors.phone ? 'cp-phone-error' : 'cp-phone-help'}
                  />
                )}
              />
              {errors.phone !== undefined ? (
                <FormError id="cp-phone-error" message={errors.phone.message} />
              ) : (
                <p id="cp-phone-help" className={s.hint}>
                  Pick your country, then type the number without the leading zero.
                </p>
              )}
            </div>

            {submitError !== null ? <StatusBanner tone="error">{submitError}</StatusBanner> : null}
          </form>
        </DialogBody>

        <DialogFooter>
          <button
            type="button"
            className={s.signOut}
            onClick={() => {
              void handleSignOut();
            }}
          >
            Sign out
          </button>
          <Button type="submit" form="complete-profile-form" loading={isSubmitting || saved}>
            Save and continue
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
